<template>
  <div>
    <el-tooltip :popper-class="'manage-rooms'" content="Manage rooms" placement="top" :enterable="false">
      <el-button
        class="btn-action"
        :class="{ active: roomVisibilityFormVisible }"
        @click="openRoomManagementWindow"
        type="text"
      >
        <IconBase
          :width="24"
          :height="24"
          :view-box="`0 0 24 24`"
          :fill="'#E7E6FA'"
        >
          <IconGear />
        </IconBase>
      </el-button>
    </el-tooltip>
    <div
      class="room-management-window--overlay"
      v-if="roomVisibilityFormVisible"
      @click="roomVisibilityFormVisible = false"
    ></div>
    <div
      class="room-management-window"
      width="50%"
      v-if="roomVisibilityFormVisible"
    >
      <div class="room-management-window--container">
        <ShareLinks
          v-if="showLinksEditor"
          :showLinksEditor="showLinksEditor"
          :sharedLinks="sharedLinks"
          @editor-closed="hideLinks"
          @link-added="addLink"
          @link-deleted="deleteSharedLink"
        />
        <div class="room-management-window__header">
          <h3 class="window-title">Manage Rooms</h3>
          <div @click="roomVisibilityFormVisible = false">
            <IconBase
              :height="24"
              :width="24"
              :view-box="`0 0 24 24`"
              :fill="'#E7E6FA'"
            >
              <IconCross />
            </IconBase>
          </div>
        </div>
        <el-tabs v-model="activeTab">
          <el-tab-pane label="All Rooms" name="allRooms">
            <template>
              <el-row type="flex" justify="end" class="room-controls">
                <div style="flex: 1; text-align: left;">
                  <el-input
                    class="roomsearch"
                    v-model="searchKeyword"
                    placeholder="Search for room"
                  >
                    <IconBase
                      slot="prefix"
                      :width="26"
                      :height="26"
                      :fill="'#E7E6FA'"
                    >
                      <IconSearch />
                    </IconBase>
                  </el-input>
                </div>
                <div @click="removeSelectedRooms" class="icon-container">
                  <IconBase
                    :width="24"
                    :height="24"
                    :view-box="`0 0 24 24`"
                    :title="'Delete'"
                    :fill="'#3887FE'"
                    ><IconBin
                  /></IconBase>
                  <div
                    class="icon--disabled"
                    v-if="selectedRooms.length === 0"
                  ></div>
                </div>
                <div @click="toggleVisibility" class="icon-container">
                  <IconBase
                    :width="24"
                    :height="24"
                    :view-box="`0 0 24 24`"
                    :title="visibilityState"
                    :fill="visibilityStateColor"
                    ><IconEye
                  /></IconBase>
                  <div
                    class="icon--disabled"
                    v-if="selectedRooms.length === 0"
                  ></div>
                </div>
                <div
                  @click="unlockSelectedRooms"
                  class="icon-container"
                  v-if="controls['UNLOCK_ROOMS']"
                >
                  <IconBase
                    :width="24"
                    :height="24"
                    :view-box="`0 0 24 24`"
                    :title="'Unlock'"
                    :fill="'#3887FE'"
                    ><IconOpenLock
                  /></IconBase>
                  <div
                    class="icon--disabled"
                    v-if="selectedRooms.length === 0"
                  ></div>
                </div>
                <div
                  @click="clearLinksInState"
                  class="icon-container"
                  v-if="controls['WHITEBOARD']"
                >
                  <IconBase
                    :width="24"
                    :height="24"
                    :view-box="`0 0 24 24`"
                    :title="'Clear Links'"
                    :fill="'#3887FE'"
                    ><IconClearLinks
                  /></IconBase>
                  <div
                    class="icon--disabled"
                    v-if="selectedRooms.length === 0"
                  ></div>
                </div>
              </el-row>
              <el-row type="flex" class="form-headers" justify="space-around">
                <el-col :span="1">
                  <el-checkbox
                    v-model="isSelectAll"
                    @change="toggleSelectAll"
                  ></el-checkbox>
                </el-col>
                <el-col :span="2"><span></span></el-col>
                <el-col class="col-display" :span="6"
                  ><span>Room name</span></el-col
                >
                <el-col class="col-display" :span="6"
                  ><span>Topic</span></el-col
                >
                <el-col class="col-display" :span="4">
                  <span>Room limit</span>
                </el-col>
                <el-col
                  class="col-display"
                  :span="2"
                  v-if="controls['WHITEBOARD']"
                  ><span>Links</span></el-col
                >
              </el-row>
              <div class="rooms">
                <template
                  v-for="room in sortRooms(
                    filterRooms(allRooms, searchKeyword)
                  )"
                >
                  <EditRoom
                    :ref="`room_${room._id}`"
                    :key="room._id"
                    :room="room"
                    :selected="selectedRooms.includes(room._id)"
                    :deleted="deletedRooms.includes(room._id)"
                    :hasSharedLinks="hasSharedLinks(room._id)"
                    @visibility-changed="selectRoom(room._id)"
                    @link-clicked="showLinks(room._id)"
                  />
                </template>
              </div>
            </template>
          </el-tab-pane>
          <el-tab-pane
            v-if="controls['CREATE_BREAKOUTS']"
            label="Quick Add Rooms"
            name="quickAddRooms"
          >
            <Breakouts
              @canceled="roomVisibilityFormVisible = false"
              @done="roomVisibilityFormVisible = false"
            />
          </el-tab-pane>
        </el-tabs>
        <div
          class="room-management-window__footer"
          v-if="activeTab === 'allRooms'"
        >
          <div class="footer__left">
            <div class="add-button" v-if="controls['CREATE_BREAKOUTS']">
              <el-button @click="addRoom" :disabled="addingRoom">
                <span class="button-text">
                  {{ addingRoom ? "Adding..." : "Add one room" }}
                </span>
              </el-button>
            </div>
          </div>
          <div class="footer__right">
            <el-button
              class="cancel close-manage-rooms"
              type="default"
              @click="roomVisibilityFormVisible = false"
              >Cancel</el-button
            >
            <el-button class="confirm" type="primary" @click="updateVisibility"
              >Done</el-button
            >
          </div>
        </div>
      </div>
    </div>

    <!-- <el-dialog :visible.sync="showDeleteAllConfirmation" width="25%">
        <span slot="title">
            <icon name="exclamation-triangle" scale="1"></icon> Confirm Deletion
        </span>
        <p>Are you sure you want to delete all the rooms?</p>
        <el-button class="cancel" type="text" @click="showDeleteAllConfirmation=false">Cancel</el-button>
        <el-button class="confirm" type="primary" @click="deleteAllRooms" :loading="saving">Confirm</el-button>
    </el-dialog> -->

    <el-dialog
      :visible.sync="showDeleteConfirmation"
      width="25%"
      :modal-append-to-body="false"
    >
      <span slot="title">
        <icon name="exclamation-triangle" scale="1"></icon> Confirm Deletion
      </span>
      <p class="text-left">Are you sure you want to delete these rooms?</p>
      <p class="text-left">This cannot be undone once you save your changes.</p>
      <div class="deleted-rooms">
        <p
          v-for="roomId in deletedRooms"
          :key="`del_${roomId}`"
          class="del-room text-left"
        >
          {{ `${roomName(roomId)}` }}
        </p>
      </div>
      <span slot="footer" class="dlg-footer">
        <el-button
          class="cancel"
          type="text"
          @click="showDeleteConfirmation = false"
          >Cancel</el-button
        >
        <el-button
          class="confirm"
          type="primary"
          @click="handleDeleteConfirmation"
          >Confirm</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import Icon from "vue-awesome/components/Icon";
import IconBase from "../IconBase";
import IconEye from "../icons/IconEye";
import IconBin from "../icons/IconBin";
import IconOpenLock from "../icons/IconOpenLock";
import IconGear from "../icons/IconGear";
import IconCross from "../icons/IconCross";
import IconClearLinks from "../icons/IconClearLinks";
import _ from "underscore";
import axios from "../../client";
import { mapState, mapGetters } from "vuex";
import EditRoom from "./EditRoom.vue";
import ShareLinks from "./ShareLinks";
import Breakouts from "./BreakOuts";
import IconSearch from "../icons/IconSearch";
import Constants from "../Constants";
import { getSocketPayload } from "../../utils";

export default {
  name: "Visibility",
  components: {
    Icon,
    EditRoom,
    IconEye,
    IconBase,
    IconBin,
    IconOpenLock,
    IconGear,
    ShareLinks,
    IconCross,
    IconClearLinks,
    Breakouts,
    IconSearch
  },

  data: function() {
    return {
      roomVisibilityFormVisible: false,
      showDeleteAllConfirmation: false,
      isSelectAll: false,
      rooms: [],
      socketRooms: [],
      selectedRooms: [],
      deletedRooms: [],
      newRooms: [],
      newSocketRooms: [],
      saving: false,
      showDeleteConfirmation: false,
      activeTab: "allRooms",
      searchKeyword: "",
      addingRoom: false,

      // Shared Links
      showLinksEditor: false,
      linksRoomId: null // Room id to show links for
    };
  },
  methods: {
    openRoomManagementWindow() {
      this.setRooms();
      this.setNewRooms();
      this.roomVisibilityFormVisible = true;
    },
    setRooms() {
      this.rooms = [];
      this.socketRooms = [];
      this.selectedRooms = [];
      this.deletedRooms = [];
      this.isSelectAll = false;
      this.searchKeyword = "";
      this.activeTab = "allRooms";
      this.roomsFromStore.forEach(room => {
        this.rooms.push(_.clone(room));
        this.socketRooms.push(JSON.parse(JSON.stringify(room)));
      });
    },
    setNewRooms() {
      this.newRooms = [];
      this.newSocketRooms = [];
    },
    selectRoom(roomId) {
      const index = this.selectedRooms.indexOf(roomId);
      if (index == -1) {
        this.selectedRooms.push(roomId);
      } else {
        this.selectedRooms.splice(index, 1);
      }
      this.updateSelectAll();
    },
    toggleVisibility() {
      const state = this.visibilityState; // copying it since it is reactive may effect below checks if used directly
      this.allRooms.forEach(room => {
        if (this.selectedRooms.includes(room._id) && room.parent) {
          room.isVisible = state === "show" ? true : false;
        }
      });
    },
    updateSelectAll() {
      let visibleRoomCount = 0;
      this.allRooms.forEach(room => {
        if (this.selectedRooms.includes(room._id)) {
          visibleRoomCount++;
        }
      });
      this.isSelectAll = visibleRoomCount === this.allRooms.length;
    },
    toggleSelectAll() {
      if (this.isSelectAll) {
        this.allRooms.forEach(room => {
          this.selectedRooms.push(room._id);
        });
      } else {
        this.selectedRooms = [];
      }
    },
    async updateRoom(room) {
      try {
        // prevent overriding of participant data
        delete room.participants;
        await axios.put("/api/rooms/" + room._id, room);
      } catch {
        this.$message({
          dangerouslyUseHTMLString: true,
          message: `Failed to save room <strong> ${room.name}</strong>. <br> Please check and try again`,
          type: "error",
          showClose: true
        });
      }
    },
    handleDeleteConfirmation() {
      this.deletedRooms.forEach(roomId => {
        const isNewRoom = this.newRooms.find(room => room._id === roomId);
        if (isNewRoom) return; // We can't delete newrooms as they wont be created by this point
        this.deleteSingleRoom(roomId);
      });
      this.showDeleteConfirmation = false;
      this.saveRooms();
      this.setRooms();
    },
    async createNewRooms() {
      try {
        await this.newRooms.forEach(async room => {
          if (this.deletedRooms.includes(room._id)) return;
          const response = await axios.post("/api/rooms/", {
            mainRoom: this.mainRoom.name,
            name: room.name,
            topic: room.topic,
            parent: this.mainRoom._id,
            rank: room.rank,
            expandedRoomParticipantsLimit: room.expandedRoomParticipantsLimit,
            isVisible: room.isVisible,
            isLocked: room.isLocked
          });
          const newRoomId = response.data._id;
          this.socket.emit(
            "NEW_ROOM_CREATED",
            getSocketPayload({ roomId: newRoomId })
          );
          const socketRoom = this.newSocketRooms.find(
            r => r.roomId === room._id
          );
          room._id = newRoomId;
          room.roomId = newRoomId;
          socketRoom._id = newRoomId;
          socketRoom.roomId = newRoomId;
          this.updateSharedLinksOnClients(newRoomId, socketRoom.sharedLinks);
        });
      } catch (err) {
        if (err.response.data.message === "TOKEN_EXPIRED") {
          this.$store.dispatch("user/refreshTokens", this.updateVisibility);
        } else {
          alert(err);
        }
      }
    },

    async saveRooms() {
      try {
        await this.rooms.forEach(room => {
          if (!this.deletedRooms.includes(room._id)) {
            const socketRoom = this.socketRooms.find(r=>r._id === room._id || r.roomId ===room._id, {});
            let updatedRoom = {...room, sharedLinks: socketRoom.sharedLinks};
            if(!room.roomId){
                updatedRoom = {...room, roomId:socketRoom.roomId};
            }
            this.updateRoom(updatedRoom);
          }
        });
      } catch (err) {
        if (err.response.data.message === "TOKEN_EXPIRED") {
          this.$store.dispatch("user/refreshTokens", this.updateVisibility);
        } else {
          alert(err);
        }
      }
      await this.createNewRooms();
      await this.updateSharedLinksForEveryRoom();
      this.roomVisibilityFormVisible = false;
      this.saving = false;
    },
    async updateVisibility() {
      this.saving = true;
      if (this.deletedRooms.length === 0) {
        this.saveRooms();
      } else {
        this.showDeleteConfirmation = true;
        // leave the save responsibility to the delete confirmation handler handleDeleteConfirmation
      }
    },
    removeSelectedRooms() {
      this.selectedRooms.forEach(roomId => {
        const newRoomIndex = this.newRooms.findIndex(
          room => room._id === roomId
        );
        const newSocketRoomIndex = this.newSocketRooms.findIndex(
          room => room._id === roomId
        );
        if (newRoomIndex !== -1) {
          // If it is the new room added on client side, remove them
          // Splice it from new rooms and socket rooms
          this.newRooms.splice(newRoomIndex, 1);
          this.newSocketRooms.splice(newSocketRoomIndex, 1);
        } else if (
          !this.deletedRooms.includes(roomId) &&
          this.mainRoom._id !== roomId
        ) {
          this.deletedRooms.push(roomId);
        }
      });
    },
    async deleteSingleRoom(roomId) {
      try {
        await axios.delete(`/api/rooms/${roomId}`);
      } catch (err) {
        if (err.response.data.message === "TOKEN_EXPIRED") {
          this.$store.dispatch("user/refreshTokens", this.updateVisibility);
        } else {
          this.$notify({
            title: "",
            message:
              "Error occured while deleting the room, Please check and try again!",
            type: "error",
            duration: 4000,
            position: "top-center"
          });
        }
      }
    },
    async deleteAllRooms() {
      this.showDeleteAllConfirmation = false;
      this.roomVisibilityFormVisible = false;
      try {
        await axios.delete("/api/rooms/parent/" + this.mainRoom._id, {
          data: {
            mainRoom: this.mainRoom.name
          }
        });
        this.$notify({
          title: "",
          message: "Successfully deleted all rooms",
          duration: 2000,
          position: "top-center"
        });
      } catch (err) {
        if (err.response.data.message === "TOKEN_EXPIRED") {
          this.$store.dispatch("user/refreshTokens", this.deleteAllRooms);
        } else {
          this.$notify({
            title: "",
            message: "Error occured while deleting all the rooms",
            type: "error",
            duration: 4000,
            position: "top-center"
          });
        }
      }
    },
    unlockSelectedRooms() {
      this.rooms.forEach(room => {
        if (this.selectedRooms.includes(room._id)) {
          room.isLocked = false;
          room.password = "";
        }
      });
    },
    showLinks(roomId) {
      this.showLinksEditor = true;
      this.linksRoomId = roomId;
    },

    hideLinks() {
      this.showLinksEditor = false;
      this.linksRoomId = null;
    },

    addLink(link) {
      if (!link) return;
      const room = this.allSocketRooms.find(
        r => r.roomId === this.linksRoomId || r._id === this.linksRoomId
      );
      room.sharedLinks.push(link);
    },

    deleteSharedLink(index) {
      const room = this.allSocketRooms.find(
        r => r.roomId === this.linksRoomId || r._id === this.linksRoomId
      );
      room.sharedLinks.splice(index, 1);
    },

    updateSharedLinksOnClients(roomId, sharedLinks) {
      this.socket.emit(
        "UPDATE_ROOM",
        getSocketPayload({
          event: "WHITEBOARD",
          mainRoom: this.mainRoom.name,
          token: this.tokens ? this.tokens["idToken"] : null,
          roomId,
          sharedLinks
        })
      );
    },

    clearLinksInState() {
      this.selectedRooms.forEach(roomId => {
        const room = this.allSocketRooms.find(room => room.roomId === roomId);
        room.sharedLinks = [];
      });
    },

    updateSharedLinksForEveryRoom() {
      this.socketRooms.forEach(room => {
        this.$store.commit("rooms/updateSharedLinks", {
          roomId: room._id,
          sharedLinks: room.sharedLinks
        });
        this.updateSharedLinksOnClients(room._id, room.sharedLinks);
      });
    },

    addRoom() {
      this.addingRoom = true;
      const lastRoom = this.sortRooms(this.allRooms).pop();
      const rank = lastRoom.rank ? lastRoom.rank + 1 : 1;
      const breakoutRoomName = `${this.mainRoom.name} ${Constants.BREAKOUT_ROOM_PREFIX} ${rank}`;
      this.addingRoom = false;
      const newRoom = {
        _id: rank, // Using rank as id so that we can identify rooms during operations
        mainRoom: this.mainRoom.name,
        name: breakoutRoomName,
        parent: this.mainRoom._id,
        rank: rank,
        topic: "",
        isLocked: false,
        isVisible: true,
        participants: []
      };
      this.newRooms.push(newRoom);
      const newSocketRoom = {
        _id: rank,
        roomId: rank,
        sharedLinks: [],
        participants: []
      };
      this.newSocketRooms.push(newSocketRoom);
      this.$nextTick(() => {
        this.scrollRoomsToEnd();
        this.addingRoom = false;
      });
    },

    scrollRoomsToEnd() {
      const container = document.querySelector(".rooms");
      container.scrollTop = container.scrollHeight;
      this.animateRoom(container.lastChild);
    },

    animateRoom(roomElement) {
      roomElement.classList.add("animate");
    }
  },

  computed: {
    ...mapState({
      currentRoom: state => state.rooms.currentRoom,
      mainRoom: state => state.rooms.mainRoom,
      roomsFromStore: state => state.rooms.rooms,
      tokens: state => state.user.tokens,
      controls: state => state.user.controls
    }),
    ...mapGetters("rooms", {
      socket: "getSocket",
      sortRooms: "sortRooms",
      filterRooms: "filterRooms",
    }),

    allRooms: function() {
      return this.rooms.concat(this.newRooms);
    },
    allSocketRooms: function() {
      return this.socketRooms.concat(this.newSocketRooms);
    },
    visibilityStateColor: function() {
      const stateToColors = {
        hide: "#3887FE",
        show: "#454546"
      };
      return stateToColors[this.visibilityState];
    },
    visibilityState: function() {
      const states = {
        hide: "hide",
        show: "show"
      };
      const visibilityMap = {};
      this.allRooms.forEach(room => {
        visibilityMap[room._id] = room.isVisible;
      });
      const visibilityArray = [];
      this.selectedRooms.forEach(roomId => {
        visibilityArray.push(visibilityMap[roomId]);
      });
      if (visibilityArray.every(visible => visible === true))
        return states.hide;
      return states.show;
    },
    roomName: function() {
      return roomId => {
        for (const room of this.allRooms) {
          if (room._id === roomId) {
            return room.name;
          }
        }
      };
    },
    sharedLinks: function() {
      const socketRoom = this.allSocketRooms.find(
        room => room.roomId === this.linksRoomId || room._id === this.linksRoomId
      );
      return (
        (socketRoom && JSON.parse(JSON.stringify(socketRoom.sharedLinks))) || []
      ); // deepcopy hack
    },

    hasSharedLinks: function() {
      return roomId => {
        const socketRoom = this.allSocketRooms.find(
          room => room.roomId === roomId || room._id === roomId
        );
        return socketRoom && socketRoom.sharedLinks && socketRoom.sharedLinks.length > 0;
      };
    }
  }
};
</script>

<style scoped>
.room-management-window--overlay {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vmax;
  background: #000;
  opacity: 0.5;
  z-index: 1;
}
.room-management-window {
  position: fixed;
  background-color: #252529;
  top: 50%;
  transform: translate(-50%, -50%);
  left: 50%;
  z-index: 19;
  max-width: 668px;
  height: 600px;
  border-radius: 8px;
}

.room-management-window--container {
  position: relative;
}
.room-management-window__header,
.room-management-window__footer {
  display: flex;
  align-items: center;
  width: calc(100% - 32px);
  background-color: #2b2b33;
  padding: 9px 16px;
}

.room-management-window__header {
  position: relative;
  justify-content: space-between;
  align-items: flex-start;
  border-radius: 8px 8px 0 0;
  height: calc(70px - 18px);
}

h3.window-title {
  margin: 0;
  font-weight: 600;
  font-size: 18px;
  line-height: 23px;
  color: #ffffff;
}

.room-management-window__footer {
  height: calc(77px - 18px);
  justify-content: space-between;
  border-radius: 0 0 8px 8px;
}

.rooms {
  position: relative;
  height: calc(600px - 70px - 77px - 48px - 45px);
  overflow-x: auto;
  scroll-behavior: smooth;
}

.rooms::-webkit-scrollbar {
  display: block !important;
  background: transparent;
  width: 6px;
}

.rooms::-webkit-scrollbar-thumb {
  background: #5e5e7a;
  border-radius: 10px;
  width: 6px;
}

>>> .el-checkbox__input .el-checkbox__inner {
  background-color: #252529;
  border: 2px solid #787691;
  /* height: 18px;
    width: 18px; */
}

>>> .el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: #50c6df;
  border: 1px solid #50c6df;
}
>>> .el-input__inner {
  color: #fff;
  background-color: #393752;
  border: 1px solid #393752;
  height: 32px;
}

>>> .el-input-number {
  line-height: 32px;
}

>>> .el-input.is-disabled .el-input__inner,
>>> .el-input-number.is-disabled .el-input .el-input__inner {
  background-color: transparent;
  border: none;
  cursor: text; /* Since allowing edit even when room is disabled */
}

>>> .el-input.is-disabled .el-input__inner::-webkit-input-placeholder,
>>> .el-input-number.is-disabled
  .el-input
  .el-input__inner::-webkit-input-placeholder {
  /* Edge */
  color: rgba(81, 81, 81, 0.8);
}

>>> .el-input.is-disabled .el-input__inner:-ms-input-placeholder,
>>> .el-input-number.is-disabled
  .el-input
  .el-input__inner:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  color: rgba(81, 81, 81, 0.8);
}

>>> .el-input.is-disabled .el-input__inner::placeholder,
>>> .el-input-number.is-disabled .el-input .el-input__inner::placeholder {
  color: rgba(81, 81, 81, 0.8);
}

>>> .roomsearch.el-input .el-input__inner {
  width: 200px;
  background: #2f2f3d;
  border-radius: 32px;
  border: none;
}

>>> .roomsearch.el-input .el-input__inner::placeholder,
>>> .roomsearch.el-input .el-input__inner::-webkit-input-placeholder,
>>> .roomsearch.el-input .el-input__inner:-ms-input-placeholder,
>>> .el-input .el-input__inner::placeholder,
>>> .el-input .el-input__inner::-webkit-input-placeholder,
>>> .el-input .el-input__inner:-ms-input-placeholder {
  color: #8d8d99;
  opacity: 0.4;
}

>>> .el-dialog__title {
  color: #fff;
}

>>> .el-dialog__header {
  background: #292a33;
  color: #fff;
  text-align: left;
  font-weight: bold;
}
>>> .el-dialog__body,
>>> .el-dialog__footer {
  background: #2b2b33;
  color: #fff;
}

>>> .el-dialog__body {
  background-color: #252529;
}

>>> .el-dialog__footer {
  text-align: left;
}
>>> .el-dialog__footer > span {
  display: flex;
  justify-content: flex-end;
}

.deleted-rooms {
  max-height: 200px;
  overflow-y: auto;
}

.clear-rooms {
  text-align: left;
  padding-left: 30px;
}
.room-visibility {
  fill: #f7f7f7;
}

.room-visibility:hover,
.room-visibility-active {
  fill: #409eff;
}

.form-headers {
  font-weight: bold;
  padding: 10px 16px;
  border-bottom: 1px solid #393752;
}

.form-headers .col-display {
  padding: 0;
}

.row-display {
  padding-bottom: 20px;
}

.col-display {
  word-break: break-all;
  text-align: left;
  margin-left: 15px;
  padding: 0 15px;
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: rgba(255, 255, 255, 0.8);
}
.icon-container {
  position: relative;
  box-shadow: 0px 3px 4px rgba(0, 0, 0, 0.14), 0px 3px 3px rgba(0, 0, 0, 0.12),
    0px 1px 8px rgba(0, 0, 0, 0.2);
}
.icon-container,
.icon-container .icon--disabled {
  margin-left: 10px;
  height: 32px;
  width: 32px;
  border-radius: 4px;
  background: #3e3e52;
  display: flex;
  justify-content: center;
  align-items: center;
}
.text-left {
  text-align: left;
}
.del-room {
  font-weight: bold;
  color: #fb5507;
}
.dlg-footer {
  display: flex;
  justify-content: flex-end;
}
.user-tooltip {
  min-width: 120px;
  max-width: 175px;
}
.ml-10 {
  margin-left: 10px;
}

.room-controls {
  padding: 8px;
  background-color: #202024;
}

.icon-container .icon--disabled {
  position: absolute;
  background-color: rgba(35, 31, 29, 0.7);
  top: 0;
  left: -10px;
  pointer-events: none;
}

.animate {
  animation-name: blink;
  animation-duration: 0.9s;
  animation-iteration-count: 1;
}

@keyframes blink {
  from {
    background-color: #292933;
    border: 2px solid #292933;
  }
  to {
    background-color: #686899;
    border: 2px solid #686899;
  }
}

div.el-tabs {
  position: relative;
  top: -23px;
}

>>> div.el-tabs__header {
  margin: 0;
  padding: 0 16px;
  background-color: #2b2b33;
}

>>> div.el-tabs__item {
  padding: 0 8px;
  height: 15px;
  font-size: 12px;
  line-height: 15px;
  font-weight: normal;
  color: #e7e6fa;
}

>>> div.el-tabs__nav {
  padding-bottom: 4px;
}

>>> div.el-tabs__item.is-active {
  color: #50c6df;
}

>>> div.el-tabs__active-bar {
  background-color: #50c6df;
}

>>> div.el-tabs__nav-wrap::after {
  background-color: transparent;
}

>>> div.el-tabs__content {
  min-width: 668px;
}

>>> .el-input__prefix {
  left: 10px;
  top: 9px;
}

.btn-action.active {
  background-color: rgba(255, 255, 255, 0.1);
}
@media (max-width: 767px) {
    .room-management-window{
        min-width: 100%;
        top: 56px;
        left: 0;
        right: 0;
        height: auto;
        bottom: 0;
        transform: translate(0, 0);
    }
    .room-management-window .modal-loading{
        height: 100%;
    }
    .room-management-window .room-management-window--container{
        height: 100%;
    }
    /* >>> div.el-tabs {
        height: calc(100% - 147px);
    } */
    >>> div.el-tabs__content{
        height: 100%;
        min-width: initial;
        width: 100%;
        overflow: auto;
    }
    >>> .el-tab-pane{
        height: 100%;
        min-width: 668px;
    }
    >>> .admin-dashboard-body{
        height: 100%;
    }
    >>> .el-table{
        height: 100%;
    }
    >>> .el-table__body-wrapper{
        height: calc(100% - 50px);
    }
    >>> .close-manage-rooms{
        display: none;
    }
    >>> button.el-button--default, button.el-button--default:hover, button.el-button--default:active,
        button.el-button--primary, button.el-button--primary:hover, button.el-button--primary:active {
        padding: 8px 4px;
        font-size: 13px;
    }
    >>> .room-management-window__footer{
        width: 100%;
        box-sizing: border-box;
        padding: 9px 8px;
    }
    #pane-quickAddRooms{
        width: 100% !important;
        min-width: initial;
    }



}

@media (hover: none) {
    .room-management-window{
        min-width: 100%;
        top: 56px;
        left: 0;
        right: 0;
        height: auto;
        bottom: 0;
        transform: translate(0, 0);
    }
    .room-management-window .modal-loading{
        height: 100%;
    }
    .room-management-window .room-management-window--container{
        height: 100%;
    }
    /* >>> div.el-tabs {
        height: calc(100% - 147px);
    } */
    >>> div.el-tabs__content{
        height: 100%;
        min-width: initial;
        width: 100%;
        overflow: auto;
    }
    >>> .el-tab-pane{
        height: 100%;
        min-width: 668px;
    }
    >>> .admin-dashboard-body{
        height: 100%;
    }
    >>> .el-table{
        height: 100%;
    }
    >>> .el-table__body-wrapper{
        height: calc(100% - 50px);
    }
    >>> .close-manage-rooms{
        display: none;
    }
    >>> button.el-button--default, button.el-button--default:hover, button.el-button--default:active,
        button.el-button--primary, button.el-button--primary:hover, button.el-button--primary:active {
        padding: 8px 4px;
        font-size: 13px;
    }
    >>> .room-management-window__footer{
        width: 100%;
        box-sizing: border-box;
        padding: 9px 8px;
    }
    #pane-quickAddRooms{
        width: 100% !important;
        min-width: initial;
    }



}

</style>
<style>
.participant-tooltip.el-tooltip__popper {
    white-space: unset !important;
}
    @media (max-width: 767px) {
        #pane-quickAddRooms .breakouts--container{
            width: 100%;
        }
        #pane-quickAddRooms .el-col-4, #pane-quickAddRooms .el-col-10, #pane-quickAddRooms .el-col-6{
            width: auto;
        }
        .manage-rooms.el-tooltip__popper {
            display: none !important;
        }
    }
    @media (hover: none) {
        #pane-quickAddRooms .breakouts--container{
            width: 100%;
        }
        #pane-quickAddRooms .el-col-4, #pane-quickAddRooms .el-col-10, #pane-quickAddRooms .el-col-6{
            width: auto;
        }
        .manage-rooms.el-tooltip__popper {
            display: none !important;
        }
    }
</style>
